import type { BinaryReader } from '../BinaryReader';
import { decodeDropdownOption, type DropdownOption } from './dropdownOption';

/**
 * Mirrors GameStars.Models.NetworkModels.NewGameConfig (CommonNetworkModels.cs:44-169).
 *
 * Two subtleties verified against the C# Serialize/Deserialize:
 *  - The three option-list COUNTS are written with e.Put(List.Count) — i.e. a 4-byte INT,
 *    NOT the ushort that PutArray would use. So decode reads getInt() for each list length.
 *  - Each Selected*Option is written (and read) ONLY when its list is non-empty.
 *
 * Wire order: SelectedGame, GameCountTitle, GameTypeTitle, GameTimeTitle,
 *   [int n1][n1×DropdownOption] (count), [int n2][...] (type), [int n3][...] (time),
 *   [int selCount? if n1>0], [int selType? if n2>0], [int selTime? if n3>0],
 *   GamePrice(long), GamePriceMax(int), GamePriceMin(int), GamePriceStep(int).
 */
export interface NewGameConfig {
  selectedGame: string;
  gameCountTitle: string;
  gameTypeTitle: string;
  gameTimeTitle: string;
  gameCountOptions: DropdownOption[];
  gameTypeOptions: DropdownOption[];
  gameTimeOptions: DropdownOption[];
  selectedGameCountOption: number;
  selectedGameTypeOption: number;
  selectedGameTimeOption: number;
  gamePrice: bigint;
  gamePriceMax: number;
  gamePriceMin: number;
  gamePriceStep: number;
}

export function decodeNewGameConfig(r: BinaryReader): NewGameConfig {
  const selectedGame = r.getString();
  // NOTE: all three titles are read first (C# reads titles before any option list).
  const gameCountTitle = r.getString();
  const gameTypeTitle = r.getString();
  const gameTimeTitle = r.getString();

  const readOptions = (): DropdownOption[] => {
    const n = r.getInt(); // INT count (hand-rolled), not a PutArray ushort
    const out: DropdownOption[] = new Array(n);
    for (let i = 0; i < n; i++) out[i] = decodeDropdownOption(r);
    return out;
  };
  const gameCountOptions = readOptions();
  const gameTypeOptions = readOptions();
  const gameTimeOptions = readOptions();

  const selectedGameCountOption = gameCountOptions.length > 0 ? r.getInt() : 0;
  const selectedGameTypeOption = gameTypeOptions.length > 0 ? r.getInt() : 0;
  const selectedGameTimeOption = gameTimeOptions.length > 0 ? r.getInt() : 0;

  const gamePrice = r.getLong();
  const gamePriceMax = r.getInt();
  const gamePriceMin = r.getInt();
  const gamePriceStep = r.getInt();

  return {
    selectedGame,
    gameCountTitle,
    gameTypeTitle,
    gameTimeTitle,
    gameCountOptions,
    gameTypeOptions,
    gameTimeOptions,
    selectedGameCountOption,
    selectedGameTypeOption,
    selectedGameTimeOption,
    gamePrice,
    gamePriceMax,
    gamePriceMin,
    gamePriceStep,
  };
}
